import { compact, get, merge, pick } from "lodash"
import { buildContributorKey } from "helpers/contributors"
import { getContributorLabel, NOT_HELD } from "connected-components/asset-view/helpers"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet, getBetaInstrumentFromOptions } from "../helpers"

const DYNAMIC_PREDICTED_BETA_FIELD = "dynamicPredicted: predicted"
const BETA_INSTRUMENT_PARAMETER = "instrument: $instrument"

function getContributorsSubquery(betaFields) {
  return `contributors {
    id
    description
    modelProviderId
    assetClass
    notHeld
    ${betaFields}
    securityBeta {
      ${betaFields}
    }
  }`
}

function getContributorsParameters({ groupByFields, shouldUseIntradayQuery, hasBetaInstrument }) {
  let parameters = ""
  if (groupByFields) {
    parameters += getGroupBySubqueryParameters(groupByFields)
  }
  if (!shouldUseIntradayQuery) {
    parameters += " includeNotHeld: $includeNotHeld"
  }
  if (!shouldUseIntradayQuery && hasBetaInstrument) {
    parameters += ` ${BETA_INSTRUMENT_PARAMETER}`
  }
  return parameters
}

function getGroupedContributorsSubquery({ betaFields, groupByFields, hasBetaInstrument = false, shouldUseIntradayQuery = false }) {
  const parameters = getContributorsParameters({ groupByFields, shouldUseIntradayQuery, hasBetaInstrument })
  return `groupedBetaContributors ${parameters ? `(${parameters})` : ""} {
    groupedContributors {
      id
      name
      ${betaFields}
      ${getContributorsSubquery(betaFields)}
    }
  }`
}

function getUngroupedContributorsSubquery({ betaFields, hasBetaInstrument = false, shouldUseIntradayQuery = false }) {
  const parameters = getContributorsParameters({ hasBetaInstrument, shouldUseIntradayQuery })
  return `betaContributors ${parameters ? `(${parameters})` : ""} {
    ${getContributorsSubquery(betaFields)}
  }`
}

export function getBetaQueryBody(groupByFields, betaFields, shouldUseIntradayQuery) {
  const simulationAlias = shouldUseIntradayQuery ? "simulation:intradaySimulation" : "simulation"
  const parameters = shouldUseIntradayQuery ? ", batchId: $batchId, on: $on" : ", base: $base, from: $date, to: $date"
  return `model (id: $modelId) {
    ${simulationAlias} (positionSet: $positionSet ${parameters}) {
      beta {
        ${betaFields}
      }
      ${groupByFields ? getGroupedContributorsSubquery({ betaFields, groupByFields, shouldUseIntradayQuery }) : getUngroupedContributorsSubquery({ betaFields, shouldUseIntradayQuery })}
    }
  }`
}

export function getDynamicPredictedBetaQueryBody(groupByFields, shouldUseIntradayQuery) {
  const innerQuery = `
    beta (${BETA_INSTRUMENT_PARAMETER}) {
      ${DYNAMIC_PREDICTED_BETA_FIELD}
    }
    ${groupByFields ? getGroupedContributorsSubquery({ betaFields: DYNAMIC_PREDICTED_BETA_FIELD, hasBetaInstrument: true, groupByFields, shouldUseIntradayQuery }) : getUngroupedContributorsSubquery({ betaFields: DYNAMIC_PREDICTED_BETA_FIELD, hasBetaInstrument: true, shouldUseIntradayQuery })}
  `

  if (shouldUseIntradayQuery) {
    return `model (id: $modelId) {
      simulation:intradaySimulation (batchId: $batchId, positionSet: $positionSet, on: $on) {
        ${innerQuery}
      }
    }`
  }

  return `model (id: $modelId) {
    simulation (positionSet: $positionSet, base: $base, from: $date, to: $date) {
      ${innerQuery}
    }
  }`
}

function mergeContributorValues(original, current) {
  return merge(original, pick(current, ["securityBeta", "predicted", "historical", "dynamicPredicted"]))
}

function parseContributorsRecursive(prefix, items, options) {
  return items.reduce((acc, item) => {
    if (item.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {},
          },
        }
      }
      return acc
    }

    const isGroup = !!item.contributors
    const key = isGroup ? item.id : buildContributorKey(item)

    if (!acc[key]) {
      acc[key] = {
        label: isGroup ? item.name : getContributorLabel(item),
        notHeld: item.notHeld,
        values: {},
        ...(isGroup && { children: {} }),
      }
    }

    acc[key].values[prefix] = mergeContributorValues(acc[key].values[prefix], item)

    if (isGroup) {
      acc[key].children = merge(
        acc[key].children,
        parseContributorsRecursive(prefix, item.contributors, options),
      )
    }

    return acc
  }, {})
}

function getQuery(groupByFields, options) {
  const { betaFields, betaInstrument, shouldUseIntradayQuery } = options
  const extraParameters = compact([
    groupByFields && getGroupByQueryParameters(groupByFields),
    !shouldUseIntradayQuery && betaInstrument && ", $instrument: PositionSetInput",
  ]).join(", ")

  if (shouldUseIntradayQuery) {
    return `
    query($modelId: String!, $batchId: Id, $positionSet: PositionSetInput!, $on: Date! ${extraParameters}) {
      beta: ${getBetaQueryBody(groupByFields, betaFields, shouldUseIntradayQuery)}
    }`
  }

  return `
  query ($modelId: String!, $positionSet: PositionSetInput!, $includeNotHeld: Boolean!, $base: [PositionSetInput], $date: Date! ${extraParameters}) {
    beta: ${getBetaQueryBody(groupByFields, betaFields, shouldUseIntradayQuery)}
    ${betaInstrument ? `dynamicPredictedBeta: ${getDynamicPredictedBetaQueryBody(groupByFields, false)}` : ""}
  }`
}

export function createBetaContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const betaInstrument = getBetaInstrumentFromOptions(options)
  const { batchId, on, shouldUseIntradayQuery } = options
  const additionalVariables = shouldUseIntradayQuery ? {
    batchId,
    on,
  } : {
    base,
    date: to,
    instrument: betaInstrument,
    includeNotHeld: !!base,
  }

  return {
    prefix,
    key: ["beta", "contributors", prefix, modelId, positionSet, base, to, groupBy, groupById, tier, betaInstrument, batchId, on, shouldUseIntradayQuery],
    query: getQuery(groupBy && groupByFields, options),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      ...groupByFields,
      ...additionalVariables,
    },
    select: (result) => {
      // We will have two sets of each contributor, one for the historical/predicted beta and one for the dynamic predicted beta
      const totalBeta = get(result, "data.beta.simulation.beta.0", {})
      const totalDynamicPredictedBeta = get(result, "data.dynamicPredictedBeta.simulation.beta.0", {})

      const betaContributorsPath = "simulation.betaContributors[0].contributors"
      const groupedBetaContributorsPath = "simulation.groupedBetaContributors[0].groupedContributors"

      const children = groupBy
        ? parseContributorsRecursive(prefix, [
          ...get(result, `data.beta.${groupedBetaContributorsPath}`, []),
          ...get(result, `data.dynamicPredictedBeta.${groupedBetaContributorsPath}`, []),
        ], options)
        : parseContributorsRecursive(prefix, [
          ...get(result, `data.beta.${betaContributorsPath}`, []),
          ...get(result, `data.dynamicPredictedBeta.${betaContributorsPath}`, []),
        ], options)

      return {
        label: "Total",
        values: {
          [prefix]: merge(totalBeta, totalDynamicPredictedBeta),
        },
        children,
      }
    },
  }
}
